import {
    StyledDecisionSupport,
    StyledEdit,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { DecisionSupportDialogShell } from '@/components/decisionSupport/DecisionSupportDialogShell';
import { DecisionSupportSelectionContent } from '@/components/decisionSupport/DecisionSupportSelectionContent';
import { VisitAssignmentClinicianRecommendationsQuery } from '@/components/decisionSupport/queries/visitAssignment.gql.types';
import { DecisionSupportSelection } from '@/components/decisionSupport/utils/decisionSupport.utils';
import { normalizeVisitReassignmentClinician } from '@/components/decisionSupport/utils/visitReassignmentRecommendations.utils';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { IconButton } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

type ClinicianOutput = NonNullable<
    VisitAssignmentClinicianRecommendationsQuery['decisionSupport']['visitAssignment']['clinicianRecommendations']
>;
type AssignmentDefinitions = ClinicianOutput['definitions'];
type AssignmentClinicianRecommendation =
    ClinicianOutput['results'][number]['clinicianRecommendation'];

type AssignmentClinicianDecisionSupportContentProps = {
    onClose(): void;
    definitions: AssignmentDefinitions | null;
    clinicianRecommendation: AssignmentClinicianRecommendation | null;
    selectedClinician: DecisionSupportSelection | null;
    setSelectedClinician(selection: DecisionSupportSelection | null): void;
};

const AssignmentClinicianDecisionSupportContent: FC<
    AssignmentClinicianDecisionSupportContentProps
> = ({
    onClose,
    definitions,
    clinicianRecommendation,
    selectedClinician,
    setSelectedClinician,
}) => {
    const normalized = useMemo(
        () => normalizeVisitReassignmentClinician(clinicianRecommendation, definitions),
        [clinicianRecommendation, definitions],
    );

    return (
        <DecisionSupportSelectionContent
            onClose={onClose}
            normalized={normalized}
            selection={selectedClinician}
            onConfirm={setSelectedClinician}
            candidateHeader='Candidate'
            itemNoun='clinicians'
        />
    );
};

export type AssignmentClinicianDecisionSupportDialogProps = {
    onClose(): void;
    definitions: AssignmentDefinitions | null;
    clinicianRecommendation: AssignmentClinicianRecommendation | null;
    selectedClinician: DecisionSupportSelection | null;
    setSelectedClinician(selection: DecisionSupportSelection | null): void;
};

export const AssignmentClinicianDecisionSupportDialog: FC<
    AssignmentClinicianDecisionSupportDialogProps
> = (props) => {
    const { onClose, ...rest } = props;

    return (
        <DecisionSupportDialogShell title='Clinician Recommendations' onClose={onClose}>
            {({ onClose }) => (
                <AssignmentClinicianDecisionSupportContent onClose={onClose} {...rest} />
            )}
        </DecisionSupportDialogShell>
    );
};

type AssignmentClinicianDecisionSupportProps = {
    definitions: AssignmentDefinitions | null;
    clinicianRecommendation: AssignmentClinicianRecommendation | null;
    selectedClinician: DecisionSupportSelection | null;
    setSelectedClinician(selection: DecisionSupportSelection | null): void;
    disabled?: boolean;
};

// Edit-icon trigger that opens the new visitAssignment clinician dialog.
export const AssignmentClinicianDecisionSupport: FC<AssignmentClinicianDecisionSupportProps> = (
    props,
) => {
    const {
        definitions,
        clinicianRecommendation,
        selectedClinician,
        setSelectedClinician,
        disabled,
    } = props;
    const { openDialog } = useDialog();

    const onClickEdit = useCallback(() => {
        openDialog(DialogType.AssignmentClinicianDecisionSupport, {
            definitions,
            clinicianRecommendation,
            selectedClinician,
            setSelectedClinician,
        });
    }, [openDialog, definitions, clinicianRecommendation, selectedClinician, setSelectedClinician]);

    const hasRows =
        (clinicianRecommendation?.recommendations.length ?? 0) > 0 ||
        (clinicianRecommendation?.incompatibleClinicians.length ?? 0) > 0;
    if (!hasRows) {
        return null;
    }

    return (
        <StyledDecisionSupport>
            <IconButton
                onClick={onClickEdit}
                disabled={disabled}
                aria-label='Edit clinician selection'
            >
                <StyledEdit />
            </IconButton>
        </StyledDecisionSupport>
    );
};
